import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";
import { Card, CardContent } from "../../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import DealTagChipEditor from "./DealTagChipEditor";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MarketplaceTable = ({ token, filters, revenueVisible = true, isSuperAdmin = false, tags = [], onTagsChanged }) => {
  const [rows, setRows] = useState([]);
  const [rowTags, setRowTags] = useState({});  // deal_id -> [tag_id]
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const pageSize = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (filters.ssp_source) params.ssp_source = filters.ssp_source;
      if (filters.dsp) params.dsp = filters.dsp;
      if (filters.deal_name) params.deal_name = filters.deal_name;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.tag_ids && filters.tag_ids.length) params.tag_ids = filters.tag_ids.join(",");

      const res = await axios.get(`${API}/reporting/marketplace/data`, {
        headers: { Authorization: `Bearer ${token}` }, params,
      });
      const fetchedRows = res.data.rows || [];
      setRows(fetchedRows);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);

      // Fetch tag assignments for distinct deal_ids on the page (super_admin only)
      if (isSuperAdmin) {
        const uniqueIds = [...new Set(fetchedRows.map((r) => r.deal_id).filter(Boolean))];
        const assignments = {};
        // The GET /tags/deals endpoint returns all deals; for pagination we do
        // targeted single-deal lookups via GET /tags/deal/{id} in parallel.
        const results = await Promise.all(
          uniqueIds.map((id) =>
            axios.get(`${API}/reporting/marketplace/tags/deal/${encodeURIComponent(id)}`, {
              headers: { Authorization: `Bearer ${token}` },
            }).then((r) => r.data).catch(() => ({ deal_id: id, tag_ids: [] }))
          )
        );
        results.forEach((r) => { assignments[r.deal_id] = r.tag_ids || []; });
        setRowTags(assignments);
      } else {
        setRowTags({});
      }
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [token, filters, page, isSuperAdmin]);

  useEffect(() => { setPage(1); }, [filters]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sortedRows = [...rows].sort((a, b) => {
    const aVal = a[sortKey] ?? "";
    const bVal = b[sortKey] ?? "";
    const cmp = typeof aVal === "number" ? aVal - bVal : String(aVal).localeCompare(String(bVal));
    return sortDir === "asc" ? cmp : -cmp;
  });

  const allColumns = [
    { key: "ssp_source", label: "SSP", w: "w-24" },
    { key: "date", label: "Date", w: "w-24" },
    { key: "deal_name", label: "Deal Name", w: "min-w-[200px]" },
    { key: "dsp", label: "DSP/Buyer", w: "min-w-[140px]" },
    { key: "impressions", label: "Imps", w: "w-24", num: true },
    { key: "clicks", label: "Clicks", w: "w-20", num: true },
    { key: "spend", label: "Spend", w: "w-24", num: true, prefix: "$" },
    { key: "revenue", label: "Revenue", w: "w-24", num: true, prefix: "$", requireRevenue: true },
    { key: "margin_pct", label: "Margin%", w: "w-20", num: true, suffix: "%", requireRevenue: true },
    { key: "cpm", label: "CPM", w: "w-20", num: true, prefix: "$" },
    { key: "ctr", label: "CTR%", w: "w-18", num: true, suffix: "%" },
    { key: "vcr", label: "VCR%", w: "w-18", num: true, suffix: "%" },
    { key: "tags", label: "Tags", w: "min-w-[180px]", requireSuperAdmin: true, custom: true },
  ];

  const columns = allColumns.filter(
    (c) => (!c.requireRevenue || revenueVisible) && (!c.requireSuperAdmin || isSuperAdmin)
  );

  const fmtVal = (val, col) => {
    if (val == null || val === "") return "-";
    if (col.num) {
      const n = Number(val);
      const formatted = col.key === "impressions" || col.key === "clicks"
        ? n.toLocaleString()
        : n.toFixed(2);
      return `${col.prefix || ""}${formatted}${col.suffix || ""}`;
    }
    return String(val);
  };

  return (
    <Card className="bg-[#1e2124] border-[#2a2d31]">
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2d31]">
          <p className="text-[#ebebeb]/50 text-xs font-mono">
            {total.toLocaleString()} rows {filters.ssp_source && `· ${filters.ssp_source}`}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1.5 rounded hover:bg-white/10 text-[#ebebeb]/60 disabled:opacity-30"
              data-testid="table-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-[#ebebeb]/50 text-xs font-mono">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-1.5 rounded hover:bg-white/10 text-[#ebebeb]/60 disabled:opacity-30"
              data-testid="table-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="w-6 h-6 text-[#ff3737] animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center p-12">
            <p className="text-[#ebebeb]/50 font-mono text-sm">No data found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#2a2d31] hover:bg-transparent">
                  {columns.map((col) => (
                    <TableHead
                      key={col.key}
                      className={`text-[#ebebeb]/50 font-mono text-xs cursor-pointer hover:text-[#ebebeb]/80 ${col.w}`}
                      onClick={() => handleSort(col.key)}
                    >
                      <span className="flex items-center gap-1">
                        {col.label}
                        {sortKey === col.key && (
                          <ArrowUpDown className="w-3 h-3 text-[#ff3737]" />
                        )}
                      </span>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRows.map((row, i) => (
                  <TableRow key={i} className="border-[#2a2d31] hover:bg-white/5" data-testid={`market-row-${i}`}>
                    {columns.map((col) => (
                      <TableCell key={col.key} className={`font-mono text-xs ${col.num ? "text-[#ebebeb]" : "text-[#ebebeb]/80"} ${col.key === "deal_name" ? "max-w-[250px] truncate" : ""}`}>
                        {col.custom && col.key === "tags" ? (
                          <DealTagChipEditor
                            token={token}
                            dealId={row.deal_id}
                            allTags={tags}
                            value={rowTags[row.deal_id] || []}
                            onChange={(next) => setRowTags((prev) => ({ ...prev, [row.deal_id]: next }))}
                            onTagsChanged={onTagsChanged}
                          />
                        ) : (
                          fmtVal(row[col.key], col)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MarketplaceTable;
